/**
 * The v2 ProseMirror schema. Locked to the corpus audit (migration §5 step 1) — every
 * node/mark the existing corpus uses is covered losslessly (AC 12.2) — PLUS the additive v2
 * concepts: commitment + attribution marks and the first cut of live-object nodes (OQ-6:
 * reminder chip, task card, decision/truth card).
 *
 * INTERNAL: the `Schema` instance is engine state and is NEVER exported from `index.ts`.
 */

import { Schema, type NodeSpec, type MarkSpec } from 'prosemirror-model';
import { commitmentMark, COMMITMENT_MARK } from './marks/commitment';
import { attributionMark, ATTRIBUTION_MARK } from './marks/attribution';
import { evidenceRefMark, EVIDENCE_REF_MARK } from './marks/evidenceRef';
import { termMark, TERM_MARK } from './marks/term';
import { chartNode, CHART_NODE } from './nodes/chart';
import { widgetNode, WIDGET_NODE } from './nodes/widget';

const nodes: Record<string, NodeSpec> = {
  doc: { content: 'block+' },

  // `partId` (corpus audit 2026-07-01): Outline's per-block collaborative id, present on block
  // nodes (paragraph 128×, heading 66×, bullet_list 40×, code_block 10×, ordered_list 4×). Declared
  // on every block node so copy-forward preserves it losslessly rather than silently dropping it.
  paragraph: {
    group: 'block',
    content: 'inline*',
    attrs: { partId: { default: null } },
    parseDOM: [{ tag: 'p' }],
    toDOM: () => ['p', 0],
  },

  heading: {
    group: 'block',
    content: 'inline*',
    attrs: { level: { default: 1 }, partId: { default: null }, collapsed: { default: null } },
    defining: true,
    parseDOM: [1, 2, 3, 4].map((level) => ({ tag: `h${level}`, attrs: { level } })),
    toDOM: (node) => [`h${node.attrs.level}`, 0],
  },

  blockquote: {
    group: 'block',
    content: 'block+',
    attrs: { partId: { default: null } },
    defining: true,
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: () => ['blockquote', 0],
  },

  code_block: {
    group: 'block',
    content: 'text*',
    marks: '',
    code: true,
    defining: true,
    attrs: { language: { default: null }, partId: { default: null } },
    parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
    toDOM: () => ['pre', ['code', 0]],
  },

  bullet_list: {
    group: 'block',
    content: 'list_item+',
    attrs: { partId: { default: null } },
    parseDOM: [{ tag: 'ul' }],
    toDOM: () => ['ul', 0],
  },

  ordered_list: {
    group: 'block',
    content: 'list_item+',
    attrs: { order: { default: 1 }, partId: { default: null } },
    parseDOM: [{ tag: 'ol' }],
    toDOM: (node) => ['ol', { start: node.attrs.order === 1 ? undefined : node.attrs.order }, 0],
  },

  list_item: {
    content: 'paragraph block*',
    attrs: { partId: { default: null } },
    defining: true,
    parseDOM: [{ tag: 'li' }],
    toDOM: () => ['li', 0],
  },

  // Corpus audit (2026-07-01): the live corpus uses the Outline node name `hr` (3254×), NOT the
  // ProseMirror-standard `horizontal_rule`. Named `hr` here so copy-forward is lossless (AC 12.2).
  hr: {
    group: 'block',
    // `markup` (corpus audit): the original markdown token (`---`/`***`), preserved for lossless
    // round-trip. `partId` for consistency with other blocks.
    attrs: { markup: { default: null }, partId: { default: null } },
    parseDOM: [{ tag: 'hr' }],
    toDOM: () => ['hr'],
  },

  image: {
    group: 'inline',
    inline: true,
    draggable: true,
    attrs: { src: {}, alt: { default: null }, title: { default: null } },
    parseDOM: [
      {
        tag: 'img[src]',
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return { src: el.getAttribute('src'), alt: el.getAttribute('alt'), title: el.getAttribute('title') };
        },
      },
    ],
    toDOM: (node) => ['img', node.attrs],
  },

  // ── Live-object nodes (OQ-6 first cut). Each carries alt/summary text so REST reads and
  //    full-text search remain valid even where a host cannot render the live object. ──
  reminder_chip: liveObject('reminder', 'span'),
  task_card: liveObject('task', 'div'),
  decision_card: liveObject('decision', 'div'),
  truth_card: liveObject('truth', 'div'),

  // brief-as-publication P1: charts carry recorded values, never a rendered picture.
  [CHART_NODE]: chartNode,
  // doc-widgets D1: the generic carrier. `chart` stays for already-published documents.
  [WIDGET_NODE]: widgetNode,

  text: { group: 'inline' },

  hard_break: {
    group: 'inline',
    inline: true,
    selectable: false,
    parseDOM: [{ tag: 'br' }],
    toDOM: () => ['br'],
  },
};

const marks: Record<string, MarkSpec> = {
  strong: {
    parseDOM: [{ tag: 'strong' }, { tag: 'b' }],
    toDOM: () => ['strong', 0],
  },
  em: {
    parseDOM: [{ tag: 'em' }, { tag: 'i' }],
    toDOM: () => ['em', 0],
  },
  // Corpus audit (2026-07-01): the corpus's inline-code mark is named `code_inline` (89×), the
  // Outline name — not the PM-standard `code`. Named `code_inline` for lossless copy-forward.
  code_inline: {
    parseDOM: [{ tag: 'code' }],
    toDOM: () => ['code', 0],
  },
  // Corpus audit (2026-07-01): Outline's comment mark (8×) carries the comment-thread id. Covering
  // the TYPE is required or PMNode.fromJSON throws on any commented span (hard losslessness fail).
  comment: {
    attrs: { id: { default: null }, userId: { default: null }, resolved: { default: false }, draft: { default: false } },
    inclusive: false,
    parseDOM: [
      {
        tag: 'span[data-comment-id]',
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return {
            id: el.getAttribute('data-comment-id'),
            userId: el.getAttribute('data-comment-user'),
            resolved: el.getAttribute('data-comment-resolved') === 'true',
          };
        },
      },
    ],
    toDOM: (mark) => [
      'span',
      {
        'data-comment-id': mark.attrs.id ?? '',
        'data-comment-user': mark.attrs.userId ?? '',
        'data-comment-resolved': String(mark.attrs.resolved ?? false),
        class: 'docit-comment',
      },
      0,
    ],
  },
  link: {
    attrs: { href: {}, title: { default: null } },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return { href: el.getAttribute('href'), title: el.getAttribute('title') };
        },
      },
    ],
    toDOM: (mark) => ['a', mark.attrs],
  },
  [COMMITMENT_MARK]: commitmentMark,
  [ATTRIBUTION_MARK]: attributionMark,
  [EVIDENCE_REF_MARK]: evidenceRefMark,
  [TERM_MARK]: termMark,
};

/** A live-object atom node with a stable `objectId`, a `kind` tag, and searchable `summary`. */
function liveObject(kind: string, tag: string): NodeSpec {
  return {
    group: 'block',
    atom: true,
    draggable: true,
    attrs: { objectId: { default: null }, kind: { default: kind }, summary: { default: '' }, data: { default: {} } },
    parseDOM: [
      {
        tag: `${tag}[data-object-kind="${kind}"]`,
        getAttrs: (dom) => {
          const el = dom as HTMLElement;
          return {
            objectId: el.getAttribute('data-object-id'),
            kind,
            summary: el.getAttribute('data-object-summary') ?? '',
          };
        },
      },
    ],
    toDOM: (node) => [
      tag,
      { 'data-object-kind': kind, 'data-object-id': node.attrs.objectId ?? '', 'data-object-summary': node.attrs.summary },
      node.attrs.summary as string,
    ],
  };
}

/** The single v2 schema instance. Kept internal — never crosses the public boundary. */
export const editorSchema = new Schema({ nodes, marks });

export type EditorSchema = typeof editorSchema;
